import React, { useState, useEffect } from 'react';
import { X, Clock, Calendar, User, Briefcase, Building2, Users, Scissors } from 'lucide-react';
import { formatInTimezone, formatTimeOnly } from '../../../../../utils/timezoneUtils';
import { useSocket } from '../../../../../hooks/useSocket';
import { useAuth } from '../../../../../context/AuthContext';

const SplitEntryModal = ({ entry, onSave, onCancel, userTimezone, convertToUTC }) => {
  const { currentUser } = useAuth();
  const { socket } = useSocket(currentUser?.id);
  // Calculate initial split point (middle of the time range)
  const calculateInitialSplitTime = () => {
    const start = new Date(entry.clock_in_time);
    const end = entry.clock_out_time ? new Date(entry.clock_out_time) : new Date();
    const middle = new Date((start.getTime() + end.getTime()) / 2);
    return middle;
  };

  // Form data for the new entry (split off portion)
  const [newEntryData, setNewEntryData] = useState({
    activity: entry.activity || '',
    business_unit: entry.business_unit || '',
    group: entry.group || '',
    project: entry.project || '',
    shift: entry.shift || '',
    team: entry.team || '',
    location: entry.location || '',
    notes: entry.notes || '',
  });

  // Split time state
  const [splitTime, setSplitTime] = useState(calculateInitialSplitTime());
  const [sliderValue, setSliderValue] = useState(50); // Percentage 0-100

  // Dropdown options
  const [activities, setActivities] = useState([]);
  const [businessUnits, setBusinessUnits] = useState([]);
  const [projects, setProjects] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  // Calculate times based on split
  const originalStart = new Date(entry.clock_in_time);
  const originalEnd = entry.clock_out_time ? new Date(entry.clock_out_time) : new Date();
  
  // Fetch dropdown options
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const headers = { 'Authorization': `Bearer ${token}` };

        // Fetch all options in parallel
        const [activitiesRes, businessUnitsRes, projectsRes, shiftsRes, teamsRes] = await Promise.all([
          fetch('/api/activities', { headers }),
          fetch('/api/business-units', { headers }),
          fetch('/api/projects', { headers }),
          fetch('/api/shifts', { headers }),
          fetch('/api/teams', { headers })
        ]);

        if (activitiesRes.ok) setActivities(await activitiesRes.json());
        if (businessUnitsRes.ok) setBusinessUnits(await businessUnitsRes.json());
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (shiftsRes.ok) setShifts(await shiftsRes.json());
        if (teamsRes.ok) setTeams(await teamsRes.json());
      } catch (error) {
        console.error('Error fetching dropdown options:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  // Listen for WebSocket updates
  useEffect(() => {
    if (!socket || !currentUser) return;

    // Handle activity updates
    const handleActivityUpdate = (data) => {
      console.log('Received activity update in SplitEntryModal:', data);
      if (data.activity) {
        setActivities(prevActivities => {
          const updatedActivities = prevActivities.map(activity =>
            activity.id === data.activity.id ? data.activity : activity
          );
          return updatedActivities;
        });
      }
    };

    // Handle project updates
    const handleProjectUpdate = (data) => {
      console.log('Received project update in SplitEntryModal:', data);
      if (data.project) {
        setProjects(prevProjects => {
          const updatedProjects = prevProjects.map(project =>
            project.id === data.project.id ? data.project : project
          );
          return updatedProjects;
        });
      }
    };

    // Register event listeners
    socket.on('activity-update', handleActivityUpdate);
    socket.on('project-update', handleProjectUpdate);
    
    return () => {
      socket.off('activity-update', handleActivityUpdate);
      socket.off('project-update', handleProjectUpdate);
    };
  }, [socket, currentUser]);

  // Update split time when slider changes
  useEffect(() => {
    const totalDuration = originalEnd.getTime() - originalStart.getTime();
    const splitPoint = originalStart.getTime() + (totalDuration * sliderValue / 100);
    setSplitTime(new Date(splitPoint));
  }, [sliderValue, originalStart, originalEnd]);

  // Format time for display in user's timezone
  const formatTime = (date) => {
    if (!date) return '';
    return formatTimeOnly(date, userTimezone).replace(' CST', '').replace(' CDT', '');
  };

  // Format date for display in user's timezone  
  const formatDate = (date) => {
    if (!date) return '';
    return formatInTimezone(date, userTimezone, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: undefined,
      minute: undefined,
      timeZoneName: undefined
    });
  };

  // Calculate duration in hours and minutes
  const calculateDuration = (start, end) => {
    const diff = end - start;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  // Handle form field changes
  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    setNewEntryData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Helper function to format date for MySQL (convert to UTC)
  const formatDateForMySQL = (date) => {
    const utcDate = convertToUTC ? convertToUTC(date) : date.toISOString();
    return utcDate.slice(0, 19).replace('T', ' ');
  };

  // Handle save
  const handleSave = () => {
    // Prepare data for updating the original entry (shorten it)
    const originalEntryUpdate = {
      clock_out_time: formatDateForMySQL(splitTime),
      // Keep all other fields the same
      activity: entry.activity,
      business_unit: entry.business_unit,
      group: entry.group,
      project: entry.project,
      shift: entry.shift,
      team: entry.team,
      location: entry.location,
      notes: entry.notes,
      date: entry.date,
      reference1: entry.reference1,
      reference2: entry.reference2,
      reference3: entry.reference3,
    };

    // Prepare data for the new entry
    const newEntry = {
      ...newEntryData,
      employee_id: entry.employee_id,
      full_name: entry.full_name, // Required field
      clock_in_time: formatDateForMySQL(splitTime),
      clock_out_time: entry.clock_out_time, // Use original end time (already in correct format)
      date: entry.date,
      // Include reference fields from original entry
      reference1: entry.reference1,
      reference2: entry.reference2,
      reference3: entry.reference3,
    };

    const splitData = {
      originalEntryId: entry.id,
      originalEntryUpdate,
      newEntry
    };
    
    onSave(splitData);
  };

  // Safety check - if no clock_out_time, don't render modal
  if (!entry.clock_out_time) {
    console.error('Cannot split an entry without clock_out_time');
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-slate-800 border border-slate-700 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-slate-900 px-6 py-4 border-b border-slate-700 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Scissors className="h-6 w-6 text-green-400" />
            <h2 className="text-xl font-semibold text-slate-200">Split Time Entry</h2>
          </div>
          <button
            onClick={onCancel}
            className="text-slate-400 hover:text-slate-200 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Original Entry Information */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-slate-200 mb-3 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-blue-400" />
              Original Entry Information
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-400">Employee:</span>
                <p className="text-slate-200 font-medium">{entry.full_name}</p>
              </div>
              <div>
                <span className="text-slate-400">Employee ID:</span>
                <p className="text-slate-200 font-medium">{entry.employee_id}</p>
              </div>
              <div>
                <span className="text-slate-400">Date:</span>
                <p className="text-slate-200 font-medium">{formatDate(originalStart)}</p>
              </div>
              <div>
                <span className="text-slate-400">Clock In:</span>
                <p className="text-slate-200 font-medium">{formatTime(originalStart)}</p>
              </div>
              <div>
                <span className="text-slate-400">Clock Out:</span>
                <p className="text-slate-200 font-medium">{entry.clock_out_time ? formatTime(originalEnd) : 'Still clocked in'}</p>
              </div>
              <div>
                <span className="text-slate-400">Total Duration:</span>
                <p className="text-slate-200 font-medium">{calculateDuration(originalStart, originalEnd)}</p>
              </div>
              <div>
                <span className="text-slate-400">Activity:</span>
                <p className="text-slate-200 font-medium">{entry.activity || '-'}</p>
              </div>
              <div>
                <span className="text-slate-400">Business Unit:</span>
                <p className="text-slate-200 font-medium">{entry.business_unit || '-'}</p>
              </div>
              <div>
                <span className="text-slate-400">Project:</span>
                <p className="text-slate-200 font-medium">{entry.project || '-'}</p>
              </div>
            </div>
          </div>

          {/* Time Split Slider */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-medium text-slate-200 mb-4">Select Split Time</h3>
            
            {/* Time display */}
            <div className="flex justify-between items-center mb-4">
              <div className="text-center">
                <p className="text-xs text-slate-400 mb-1">Original Entry</p>
                <p className="text-sm font-medium text-blue-400">{formatTime(originalStart)} - {formatTime(splitTime)}</p>
                <p className="text-xs text-slate-500 mt-1">{calculateDuration(originalStart, splitTime)}</p>
              </div>
              
              <div className="flex-1 px-4">
                <div className="h-px bg-slate-600 relative">
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 h-3 w-3 rounded-full"></div>
                </div>
              </div>
              
              <div className="text-center">
                <p className="text-xs text-slate-400 mb-1">New Entry</p>
                <p className="text-sm font-medium text-green-400">{formatTime(splitTime)} - {formatTime(originalEnd)}</p>
                <p className="text-xs text-slate-500 mt-1">{calculateDuration(splitTime, originalEnd)}</p>
              </div>
            </div>

            {/* Slider */}
            <div className="relative">
              <input
                type="range"
                min="10"
                max="90"
                value={sliderValue}
                onChange={(e) => setSliderValue(parseInt(e.target.value))}
                className="w-full h-2 bg-slate-600 rounded-lg appearance-none cursor-pointer slider"
                style={{
                  background: `linear-gradient(to right, #3B82F6 0%, #3B82F6 ${sliderValue}%, #10B981 ${sliderValue}%, #10B981 100%)`
                }}
              />
              <div className="flex justify-between text-xs text-slate-400 mt-2">
                <span>{formatTime(originalStart)}</span>
                <span className="text-yellow-400 font-medium">{formatTime(splitTime)}</span>
                <span>{formatTime(originalEnd)}</span>
              </div>
            </div>
          </div>

          {/* New Entry Configuration */}
          <div className="bg-slate-900 border border-slate-700 rounded-lg p-4">
            <h3 className="text-lg font-medium text-slate-200 mb-4 flex items-center">
              <Briefcase className="h-5 w-5 mr-2 text-green-400" />
              New Entry Configuration
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Activity */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Activity</label>
                <select
                  name="activity"
                  value={newEntryData.activity}
                  onChange={handleFieldChange}
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={loading}
                >
                  <option value="">Select activity</option>
                  {activities.filter(activity => activity.is_active).map(activity => (
                    <option key={activity.id} value={activity.name}>
                      {activity.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Business Unit */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Business Unit</label>
                <select
                  name="business_unit"
                  value={newEntryData.business_unit}
                  onChange={handleFieldChange}
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={loading}
                >
                  <option value="">Select business unit</option>
                  {businessUnits.filter(unit => unit.is_active).map(unit => (
                    <option key={unit.id} value={unit.identifier}>
                      {unit.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Project */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Project</label>
                <select
                  name="project"
                  value={newEntryData.project}
                  onChange={handleFieldChange}
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={loading}
                >
                  <option value="">Select project</option>
                  {projects.filter(project => project.is_active).map(project => (
                    <option key={project.id} value={project.name}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Team */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Team</label>
                <select
                  name="team"
                  value={newEntryData.team}
                  onChange={handleFieldChange}
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={loading}
                >
                  <option value="">Select team</option>
                  {teams.map(team => (
                    <option key={team.id} value={team.name}>
                      {team.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Shift */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Shift</label>
                <select
                  name="shift"
                  value={newEntryData.shift}
                  onChange={handleFieldChange}
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                  disabled={loading}
                >
                  <option value="">Select shift</option>
                  {shifts.map(shift => (
                    <option key={shift.id} value={shift.name}>
                      {shift.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Location</label>
                <input
                  type="text"
                  name="location"
                  value={newEntryData.location}
                  onChange={handleFieldChange}
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Group */}
              <div>
                <label className="block text-sm font-medium text-slate-400 mb-1">Group</label>
                <input
                  type="text"
                  name="group"
                  value={newEntryData.group}
                  onChange={handleFieldChange}
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              {/* Notes */}
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-400 mb-1">Notes</label>
                <textarea
                  name="notes"
                  value={newEntryData.notes}
                  onChange={handleFieldChange}
                  rows="3"
                  className="w-full bg-slate-700 text-slate-200 border border-slate-600 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="bg-slate-900 px-6 py-4 border-t border-slate-700 flex justify-between items-center">
          <div className="text-sm text-slate-400">
            This will create a new entry from <span className="text-green-400 font-medium">{formatTime(splitTime)}</span> to <span className="text-green-400 font-medium">{formatTime(originalEnd)}</span>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="px-4 py-2 bg-slate-700 text-slate-300 rounded hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-slate-500 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors flex items-center"
            >
              <Scissors className="h-4 w-4 mr-2" />
              Split Entry
            </button>
          </div>
        </div>
      </div>

      {/* Custom styles for the slider */}
      <style jsx>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 20px;
          height: 20px;
          background: #FBBF24;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1E293B;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
        
        .slider::-moz-range-thumb {
          width: 20px;
          height: 20px;
          background: #FBBF24;
          cursor: pointer;
          border-radius: 50%;
          border: 2px solid #1E293B;
          box-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }
      `}</style>
    </div>
  );
};

export default SplitEntryModal;